import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
  Typography,
  Row,
  Tooltip,
} from 'antd';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import {
  getBloquesDeTerrenoYConstruccion,
  patchBloqueDeTerrenoYConstruccion,
  postBloqueDeTerrenoYConstruccion,
  deleteBloqueDeTerrenoYConstruccion,
} from 'api/catastro/bloquesDeTerrenoYConstruccion';
import { getValoresDeTerrenosYConstrucciones } from 'api/catastro';
import NumericInput from 'components/NumericInput';
import { QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons';
import toCurrency from 'utils/toCurrency';
import { toFixed, toInteger, toMoment } from 'utils/normalizers';
import { patchPredio, dateKeysPredio, getPredio } from 'api/catastro/predio';
import styled from 'styled-components';
import formatValues from 'utils/formatValues';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import ValoresDePredio from 'components/Catalogos/ValoresDePredio';
import getAllPages from 'utils/getAllPages';

export const permissionConfig = {
  title: 'Bloques de Terreno',
  model: 'bloquedeterrenoyconstruccion',
  catalogs: [
    'valordeterrenoyconstruccion',
    'meritoydemeritodepredio',
    'meritoydemerito',
    'tasadepredio',
  ],
};

const rules = {
  required: [{ required: true }],
  anio: [
    { required: true },
    {
      validator: async (_, val) => {
        if (!val) return;
        if (val?.toString().length !== 4) throw new Error('Ingrese un año válido');
      },
    },
  ],
};
const anio = toMoment(new Date()).format('YYYY');
const tipo_de_bloque = true;

const BloqueDeTerreno = ({
  predio = {},
  mutable,
  permission,
  onUpdatePredio,
  fetchCallback,
  noAfectarPadron,
  callback,
  changePredioPermission,
}) => {
  const [form] = Form.useForm();
  const _anio = Form.useWatch('anio', form);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [valoresDeZonas, setValoresDeZonas] = useState([]);

  const validAnio = _anio?.toString().length === 4;

  const filteredValoresDeZonas = useMemo(() => {
    if (!validAnio) return [];
    return valoresDeZonas.filter((v) => v.anio.toString() === _anio.toString());
  }, [_anio, valoresDeZonas]);

  const superficie_total_del_terreno = useMemo(
    () => toFixed(data.reduce((accum, curr) => accum + toFixed(curr.area, 3), 0), 3),
    [data],
  );

  const valor_del_terreno = useMemo(
    () => toFixed(data.reduce((accum, curr) => accum + toFixed(curr.importe, 3), 0), 3),
    [data],
  );
  const params = { predio: predio.id, tipo_de_bloque };

  const fetchData = async () => {
    if (!predio) return;
    setLoading(true);
    if (noAfectarPadron) {
      const _predio = await getPredio(predio.id, false);
      const json = _predio.json_predio || removeIndexes(_predio);
      json.bloques_de_terreno = json.bloques_de_terreno
        || await getBloquesDeTerrenoYConstruccion(params);

      json.bloques_de_terreno_a_eliminar = json.bloques_de_terreno_a_eliminar || [];
      json.bloques_de_terreno_a_agregar = json.bloques_de_terreno_a_agregar || [];
      json.bloques_de_terreno_a_modificar = json.bloques_de_terreno_a_modificar || [];
      const json_predio = JSON.stringify(formatValues(json, { clean: false }, dateKeysPredio));
      if (mutable) {
        _predio.json_predio = _predio.json_predio_raw !== json_predio
          ? (await patchPredio(predio.id, { json_predio }, false))?.json_predio
          : json;
      }
      setData(json.bloques_de_terreno);
      if (fetchCallback) fetchCallback(json.bloques_de_terreno, _predio.json_predio);
      setLoading(false);
      return;
    }

    const _data = await getBloquesDeTerrenoYConstruccion(params);
    setData(_data);
    if (fetchCallback) fetchCallback(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const _valores = await getAllPages(
        getValoresDeTerrenosYConstrucciones,
        { tipo_bloque: tipo_de_bloque, page_size: 10000 },
      );
      await fetchData();
      if (mounted) {
        setValoresDeZonas(_valores);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [predio?.id]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.predio = predio.id;
      values.tipo_de_bloque = tipo_de_bloque;
      const [key] = selectedRowKeys;
      if (noAfectarPadron) {
        let json_predio = { ...predio };
        if (key) {
          const idx = json_predio.bloques_de_terreno.findIndex((e) => e.id === key);
          json_predio.bloques_de_terreno[idx] = {
            ...json_predio.bloques_de_terreno[idx],
            ...values,
          };
        } else {
          values.id = `temp_id_${(new Date()).toISOString()}`;
          json_predio.bloques_de_terreno.push(values);
        }
        if (typeof key === 'number' && !json_predio.bloques_de_terreno_a_modificar.includes(key)) {
          json_predio.bloques_de_terreno_a_modificar.push(key);
        } else if (!key) {
          json_predio.bloques_de_terreno_a_agregar.push(values.id);
        }
        json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
        const saved = await patchPredio(predio.id, { json_predio }, false);
        if (!saved) {
          setLoading(false);
          return;
        }
        if (callback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
        return;
      }
      const saved = await (key
        ? patchBloqueDeTerrenoYConstruccion(key, values)
        : postBloqueDeTerrenoYConstruccion(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const onClickEdit = () => setVisible(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = false;
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.bloques_de_terreno = json_predio.bloques_de_terreno
        .filter((e) => e.id !== selectedRowKeys[0]);
      if (typeof key === 'number') {
        json_predio.bloques_de_terreno_a_eliminar.push(key);
        if (json_predio.bloques_de_terreno_a_modificar.includes(key)) {
          json_predio.bloques_de_terreno_a_modificar = json_predio
            .bloques_de_terreno_a_modificar.filter((e) => e !== key);
        }
      } else {
        json_predio.bloques_de_terreno_a_agregar = json_predio
          .bloques_de_terreno_a_agregar.filter((e) => e !== key);
      }
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      deleted = await patchPredio(predio.id, { json_predio }, false);
    } else {
      deleted = await deleteBloqueDeTerrenoYConstruccion(key);
    }
    if (deleted) {
      if (callback) await callback('Eliminado correctamente');
      await fetchData();
      setVisibleAlert(false);
      onCancel();
    }
    setLoading(false);
  };

  const rowSelection = mutable ? {
    selectedRowKeys,
    type: 'radio',
  } : undefined;
  const renderValorDeZona = (e) => [e.clave, e.descripcion, toCurrency(e.valor, false, 3)]
    .join(' - ');

  const columns = [
    {
      titleText: 'No. Bloque',
      key: 'numero_de_bloque',
      dataIndex: 'numero_de_bloque',
      width: 120,
      render: (v, _, i) => v || i + 1,
    },
    {
      titleText: 'Valor Zona',
      key: 'valor',
      dataIndex: 'valor',
      width: 500,
      render: (valor) => renderValorDeZona(valoresDeZonas.find((v) => v.id === valor)),
    },
    {
      titleText: 'Matríz Bloque',
      key: 'matriz_bloque',
      dataIndex: 'matriz_bloque',
      width: 150,
    },
    {
      titleText: 'Año',
      key: 'anio',
      dataIndex: 'anio',
      width: 100,
    },
    {
      titleText: 'Factor Ajuste',
      key: 'factor_ajuste',
      dataIndex: 'factor_ajuste',
      width: 200,
      render: (val) => (val ? toFixed(val, 2) : null),
    },
    {
      titleText: 'Pisos',
      key: 'pisos',
      dataIndex: 'pisos',
      width: 100,
    },
    {
      titleText: 'Demérito',
      key: 'demerito',
      dataIndex: 'demerito',
      width: 200,
      render: (val) => (val ? toFixed(val, 3) : null),
    },
    {
      titleText: 'Valor Unitario',
      key: 'valor_unitario',
      dataIndex: 'valor_unitario',
      width: 200,
      render: (val) => (val ? toFixed(val, 3) : null),
    },
    {
      titleText: 'Área',
      key: 'area',
      dataIndex: 'area',
      width: 200,
      render: (val) => (val ? toFixed(val, 3) : null),
    },
    {
      titleText: 'Importe',
      key: 'importe',
      dataIndex: 'importe',
      width: 200,
      align: 'right',
      render: (val) => toCurrency(val, false, 3),
    },
  ];

  const updatePredio = async () => {
    setLoading(true);
    const { valor_de_la_construccion = 0 } = predio;
    const valor_catastral_calculado = toFixed(valor_del_terreno + valor_de_la_construccion, 3);
    let values = {
      superficie_total_del_terreno,
      valor_del_terreno,
      valor_catastral_calculado,
      valor_catastral: valor_catastral_calculado,
    };
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      Object.keys(values).forEach((key) => {
        json_predio[key] = values[key];
      });
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      values = { json_predio };
    }
    const saved = await patchPredio(predio.id, values, false);
    if (saved) {
      onUpdatePredio(noAfectarPadron ? { json_predio: saved.json_predio } : {
        superficie_total_del_terreno: saved.superficie_total_del_terreno,
        valor_del_terreno: saved.valor_del_terreno,
        valor_catastral_calculado: saved.valor_catastral_calculado,
        valor_catastral: saved.valor_catastral,
        zona_homogenea: saved.zona_homogenea,
        tipo_de_predio: saved.tipo_de_predio,
      });
    }
    setLoading(false);
  };

  const onValuesChange = (changed) => {
    if (['valor', 'valor_unitario', 'area', 'factor_ajuste'].some((k) => k in changed)) {
      const { valor_unitario, area, factor_ajuste } = form.getFieldsValue();
      const importe = valor_unitario && area && factor_ajuste
        ? valor_unitario * area * factor_ajuste : null;
      form.setFieldValue('importe', importe);
    }
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Bloques de Terreno
        </Typography.Title>
        <br />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
            onClickExpand: onClickEdit,
          }}
          allowExpand={permission?.view && !permission?.change}
          customActions={[
            {
              onClick: updatePredio,
              icon: <SyncOutlined />,
              text: 'Actualizar Predio',
              disabledBySelection: false,
              hasPermission: changePredioPermission,
            },
          ]}
          noControls={!mutable}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          mobileColIndex={0}
          permission={permission}
        />
        <Row gutter={10} style={{ marginTop: 10 }}>
          <Col xs={24} sm={24} md={12}>
            <FormItem
              label="Total Área"
              name="superficie_total_del_terreno"
              $currValue={toCurrency(predio?.superficie_total_del_terreno, true, 3)}
              $warn={superficie_total_del_terreno !== predio?.superficie_total_del_terreno}
            >
              <Input
                disabled
                placeholder={toCurrency(superficie_total_del_terreno, true, 3)}
              />
            </FormItem>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <FormItem
              label="Total Importe"
              name="valor_del_terreno"
              $currValue={toCurrency(valor_del_terreno, false, 3)}
              $warn={valor_del_terreno !== predio?.valor_del_terreno}
            >
              <Input
                disabled
                placeholder={toCurrency(predio?.valor_del_terreno, false, 3)}
              />
            </FormItem>
          </Col>
        </Row>
        <ValoresDePredio
          predio={predio}
          mutable={mutable}
          onUpdatePredio={onUpdatePredio}
          noAfectarPadron={noAfectarPadron}
          changePredioPermission={changePredioPermission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Bloque`}
              onFinish={permission?.change ? onFinish : undefined}
              onCancel={onCancel}
              loading={loading}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          destroyOnClose={false}
          forceRender
        >
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={() => (permission?.change ? onFinish() : undefined)}
              initialValues={{ anio, factor_ajuste: 1 }}
              onValuesChange={onValuesChange}
            >
              <Row gutter={10}>
                <Col span={24}>
                  <Form.Item
                    label="Valor Zona"
                    name="valor"
                    rules={rules.required}
                  >
                    <Select
                      render={renderValorDeZona}
                      dataSource={filteredValoresDeZonas}
                      disabled={!mutable || !permission?.change}
                      onChange={(valor) => {
                        const _valor = filteredValoresDeZonas.find((v) => v.id === valor);
                        if (_valor) {
                          form.setFieldsValue({
                            matriz_bloque: _valor.matriz_valor,
                            valor_unitario: _valor.valor,
                          });
                        }
                        onValuesChange({ valor });
                      }}
                      notFoundContent={!validAnio && 'Capture un año válido'}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Matríz Bloque" name="matriz_bloque">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <NumericInput
                    label="Valor Unitario"
                    name="valor_unitario"
                    precision={3}
                    disabled
                    decimal
                    currency
                  />
                </Col>
                <Col span={24}>
                  <NumericInput
                    label="Área"
                    name="area"
                    rules={rules.required}
                    disabled={!mutable || !permission?.change}
                    precision={3}
                    decimal
                  />
                </Col>
                <Col span={24}>
                  <NumericInput
                    label="Importe"
                    name="importe"
                    precision={3}
                    disabled
                    currency
                    decimal
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="Año"
                    name="anio"
                    normalize={(v) => toInteger(v)}
                    rules={rules.anio}
                  >
                    <Input allowClear disabled={!permission?.change} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <NumericInput
                    label="Factor Ajuste"
                    name="factor_ajuste"
                    disabled={!mutable || !permission?.change}
                    precision={2}
                    decimal
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Altura"
                    name="altura"
                    precision={4}
                    decimal
                    disabled={!permission?.change}
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Longitud"
                    name="longitud"
                    precision={4}
                    decimal
                    disabled={!permission?.change}
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Pendiente"
                    name="pendiente"
                    precision={4}
                    decimal
                    disabled={!permission?.change}
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item label="Ascendente" name="ascendente">
                    <Select trueFalse disabled={!permission?.change} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Subpredio"
                    name="subpredio"
                    disabled={!permission?.change}
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item label="Privativa" name="privativa">
                    <Select trueFalse disabled={!permission?.change} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Demérito"
                    name="demerito"
                    precision={4}
                    decimal
                    disabled={!permission?.change}
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <NumericInput
                    label="Pisos"
                    name="pisos"
                    disabled={!permission?.change}
                  />
                </Col>
                <Col span={24}>
                  <Form.Item name="observaciones" label="Observaciones">
                    <Input.TextArea autoSize={{ minRows: 2 }} disabled={!permission?.change} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="el Bloque de Terreno seleccionado"
      />
    </>
  );
};

const FormItem = styled(Form.Item).attrs((props) => ({
  ...props,
  hasFeedback: true,
  validateStatus: props.$warn && 'warning',
  help: props.$warn && (
    <>
      No concuerda con valor guardado en Predio
      {' '}
      <Tooltip title={props.$currValue} placement="bottom">
        <QuestionCircleOutlined />
      </Tooltip>
    </>
  ),
}))`
  input {
    text-align: end;
    ::placeholder {
      color: #000 !important;
    }
  }
`;

BloqueDeTerreno.propTypes = {
  predio: PropTypes.shape().isRequired,
  mutable: PropTypes.bool,
  onUpdatePredio: PropTypes.func.isRequired,
  fetchCallback: PropTypes.func,
  permission: PropTypes.shape().isRequired,
  callback: PropTypes.func,
  noAfectarPadron: PropTypes.bool,
  changePredioPermission: PropTypes.bool,
};

BloqueDeTerreno.defaultProps = {
  mutable: true,
  fetchCallback: null,
  callback: null,
  noAfectarPadron: false,
  changePredioPermission: false,
};

export default BloqueDeTerreno;
